/**
 * AI アシスタントのパネル（画面右から出る。モバイルでは全画面）
 *
 * 開くたびにホーム（用途のボタン）から始まる。用途を選び、段（イベント・選択肢）を
 * 数タップで選ぶと結果が出る。結果のあとは、次の一手・切り替え・直し・その用途の範囲の
 * 会話で続ける。範囲外は［別の会話で続ける］（自由入力の会話）へ。
 *
 * - 会話の履歴はサーバーが持つ。送るのは会話 ID と今回の発言・段の値だけ
 * - 変更はすべて確認カード（ChatActionCard）で利用者が確定したときだけ保存される
 * - 今月の残りは円で見せ、押すと内訳（ChatUsageRing）
 *
 * 設計: docs/decisions/ai-chat-guided-flows.html / 仕様: docs/features/ai-chat.md
 */

import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, PaperAirplaneIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ChatAllFlows from '@/components/chat/ChatAllFlows';
import ChatEntryItem from '@/components/chat/ChatEntryItem';
import ChatFlowSteps, { type FlowSlots } from '@/components/chat/ChatFlowSteps';
import ChatHelpTree from '@/components/chat/ChatHelpTree';
import ChatHome from '@/components/chat/ChatHome';
import ChatUsageRing from '@/components/chat/ChatUsageRing';
import ChatHistory from '@/components/chat/ChatHistory';
import ChatInputExamples from '@/components/chat/ChatInputExamples';
import AiMark from '@/components/chat/AiMark';
import ChatUsageNotice, { ChatUpgradeLink } from '@/components/chat/ChatUsageNotice';
import { DisabledReason } from '@/components/common/DisabledReason';
import {
  CHAT_ACTION_STATUS,
  CHAT_ENTRY_POINT,
  CHAT_ERROR_CODE,
  CHAT_FLOW_ID,
  CHAT_MESSAGE_MAX_LENGTH,
  CHAT_INPUT_MAX_HEIGHT_PX,
  CHAT_RETENTION_DAYS,
  CHAT_USAGE_LEVEL,
  CHAT_VIEW,
} from '@/constants/chat';
import { PLAN_TYPE } from '@/constants/planTypes';
import { chatUpgradePlan, chatUsageLevel, formatResetDate } from '@/utils/chatUsage';
import { useAllowPinchZoom } from '@/hooks/useAllowPinchZoom';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/hooks/useChat';
import { usePolicyNotice } from '@/hooks/usePolicyNotice';
import { useUser } from '@/hooks/useUser';
import { chatQueryKeys } from '@/queryKeys/chat';
import {
  ChatApiError,
  cancelChatAction,
  confirmChatAction,
  getChatConversation,
  getChatHome,
  runChatFlow,
  sendChatClientEvent,
  sendChatFeedback,
  sendChatMessage,
  type ChatActionResult,
  type ChatConversationResponse,
  type ChatFlowButton,
  type ChatFlowRunInput,
  type ChatFlowRunRequest,
  type ChatFollowUp,
  type ChatHomeEvent,
  type ChatMessageResponse,
  type ChatUsage,
} from '@/services/chat';
import { copyText } from '@/utils/toolShare';

type PanelState =
  | { view: typeof CHAT_VIEW.HOME }
  | { view: typeof CHAT_VIEW.ALL }
  | { view: typeof CHAT_VIEW.HELP }
  | { view: typeof CHAT_VIEW.HISTORY }
  | {
      view: typeof CHAT_VIEW.STEPS;
      flowId: string;
      slots: Record<string, string>;
      entry: NonNullable<ChatFlowRunRequest['entry']>;
      parentId: string | null;
    }
  | {
      view: typeof CHAT_VIEW.CONVERSATION;
      conversationId: string | null;
      /** これまでの会話から開いたときは、戻るで一覧に戻る */
      backTo?: typeof CHAT_VIEW.HISTORY;
    };

/** 発言・実行の結果を会話のキャッシュに足す */
function appendResponse(
  previous: ChatConversationResponse | undefined | null,
  response: ChatMessageResponse,
  flow: { flowId: string | null; flowSlots: FlowSlots } | null,
): ChatConversationResponse {
  return {
    conversationId: response.conversationId,
    contextEventId: previous?.contextEventId ?? null,
    flowId: flow?.flowId ?? previous?.flowId ?? null,
    flowSlots: flow?.flowSlots ?? previous?.flowSlots ?? {},
    entries: [...(previous?.entries ?? []), ...(response.entries ?? [])],
    pendingAction: response.pendingAction ?? null,
    canContinue: previous?.canContinue ?? true,
    usage: response.usage,
  };
}

/** 確定・取り消しの結果を会話のキャッシュに反映する */
function applyActionResult(
  previous: ChatConversationResponse | undefined | null,
  result: ChatActionResult,
): ChatConversationResponse | undefined | null {
  if (!previous) return previous;
  return {
    ...previous,
    entries: [
      ...(previous.entries ?? []).map((entry) =>
        entry.action?.actionId === result.actionId ? { ...entry, actionStatus: result.status } : entry,
      ),
      result.entry,
    ],
    pendingAction: null,
  };
}

function remainingSteps(flow: ChatFlowButton, slots: Record<string, string>): number {
  return (flow.steps ?? []).filter((step) => !(step.id in slots)).length;
}

function PanelBody() {
  const chat = useChat();
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const queryClient = useQueryClient();
  const [state, setState] = useState<PanelState>({ view: CHAT_VIEW.HOME });
  const [draft, setDraft] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // 入力欄は 1 行から始め、書いた分だけ広げる（上限を超えたら欄の中でスクロール）
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, CHAT_INPUT_MAX_HEIGHT_PX)}px`;
  }, [draft]);
  const [sendingText, setSendingText] = useState<string | null>(null);
  const [followUps, setFollowUps] = useState<ChatFollowUp[]>([]);
  const stepRef = useRef(0);
  // 画面の中の段（使い方の木の階層・用途の段の番号）。スクロール位置を段ごとに覚えるのに使う
  const [subKey, setSubKey] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef(new Map<string, number>());
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const contextEventId = chat?.contextEventId ?? null;
  const entry = chat?.entry ?? CHAT_ENTRY_POINT.OTHER;

  const { refreshUser } = useUser();
  const { openNotice } = usePolicyNotice();
  // 開いているあいだに規約・ポリシーの版が上がった（API が同意・確認を求めた）ら、パネルを閉じて告知を出す。
  // 同意が要る改定なら、読み直した利用者情報を見て OnboardingGuard が同意画面へ送る
  const handleConsentRequired = (error: unknown): boolean => {
    if (!(error instanceof ChatApiError && error.code === CHAT_ERROR_CODE.CONSENT_REQUIRED)) return false;
    chat?.closeChat();
    openNotice();
    void refreshUser();
    return true;
  };

  const homeQuery = useQuery({
    queryKey: chatQueryKeys.home(userId, contextEventId),
    queryFn: async () => {
      try {
        return await getChatHome(contextEventId, entry);
      } catch (error) {
        handleConsentRequired(error);
        throw error;
      }
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
  const home = homeQuery.data;
  const flows = new Map((home?.flows ?? []).map((flow) => [flow.flowId, flow]));
  const events = new Map<string, ChatHomeEvent>((home?.events ?? []).map((e) => [e.eventId, e]));

  const conversationId = state.view === CHAT_VIEW.CONVERSATION ? state.conversationId : null;
  const conversationQuery = useQuery({
    queryKey: chatQueryKeys.conversation(userId, conversationId ?? ''),
    queryFn: async () => {
      try {
        return await getChatConversation(conversationId ?? '');
      } catch (error) {
        // 30 日で消えた会話は、無かったものとして扱う
        if (error instanceof ChatApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!conversationId,
    staleTime: Infinity,
    retry: false,
  });
  const conversation = conversationId ? conversationQuery.data : null;
  const conversationFlow = conversation?.flowId ? flows.get(conversation.flowId) : undefined;
  const usage: ChatUsage | undefined = conversation?.usage ?? home?.usage;

  const showConversation = (response: ChatMessageResponse, flow: { flowId: string | null; flowSlots: FlowSlots } | null) => {
    queryClient.setQueryData<ChatConversationResponse | null>(
      chatQueryKeys.conversation(userId, response.conversationId),
      (previous) => appendResponse(response.conversationId === conversationId ? previous : null, response, flow),
    );
    queryClient.setQueryData(chatQueryKeys.usage(userId), response.usage);
    void queryClient.invalidateQueries({ queryKey: chatQueryKeys.breakdown(userId) });
    chat?.setConversationId(response.conversationId);
    setState({ view: CHAT_VIEW.CONVERSATION, conversationId: response.conversationId });
  };

  const handleError = (error: unknown) => {
    if (handleConsentRequired(error)) return;
    if (error instanceof ChatApiError && error.code === CHAT_ERROR_CODE.CONVERSATION_FULL && conversationId) {
      queryClient.setQueryData<ChatConversationResponse | null>(
        chatQueryKeys.conversation(userId, conversationId),
        (previous) => (previous ? { ...previous, canContinue: false } : previous),
      );
    }
    if (error instanceof ChatApiError && error.code === CHAT_ERROR_CODE.BUDGET_EXCEEDED) {
      void queryClient.invalidateQueries({ queryKey: chatQueryKeys.home(userId, contextEventId) });
    }
    toast.error(error instanceof Error ? error.message : 'エラーが発生しました');
  };

  const runMutation = useMutation({
    mutationFn: (input: { flowId: string; body: ChatFlowRunInput }) => runChatFlow(input.flowId, input.body),
    onSuccess: (response, input) => {
      setFollowUps(response.followUps ?? []);
      showConversation(response, { flowId: input.flowId, flowSlots: input.body.slots ?? {} });
    },
    onError: handleError,
  });

  const sendMutation = useMutation({
    mutationFn: (input: { message: string; conversationId: string | null }) =>
      sendChatMessage({ message: input.message, conversationId: input.conversationId, contextEventId: null }),
    onMutate: (input) => setSendingText(input.message),
    onSuccess: (response) => {
      setFollowUps([]);
      showConversation(response, null);
      setDraft('');
    },
    onError: handleError,
    onSettled: () => setSendingText(null),
  });

  const actionMutation = useMutation({
    mutationFn: (input: { actionId: string; confirm: boolean; acknowledge: boolean }) =>
      input.confirm ? confirmChatAction(input.actionId, input.acknowledge) : cancelChatAction(input.actionId),
    onSuccess: (result) => {
      if (conversationId) {
        queryClient.setQueryData<ChatConversationResponse | null | undefined>(
          chatQueryKeys.conversation(userId, conversationId),
          (previous) => applyActionResult(previous, result),
        );
      }
      void queryClient.invalidateQueries({ queryKey: chatQueryKeys.home(userId, contextEventId) });
      if (result.status === CHAT_ACTION_STATUS.CONFIRMED) toast.success('保存しました');
    },
    onError: (error) => {
      if (handleConsentRequired(error)) return;
      if (conversationId) {
        void queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(userId, conversationId) });
      }
      toast.error(error instanceof Error ? error.message : 'エラーが発生しました');
    },
  });

  // 新しい発言が増えたら一番下までスクロールする
  const entryCount = conversation?.entries?.length ?? 0;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [entryCount, sendingText]);

  // 画面ごとにスクロール位置を覚える。初めて開く画面は一番上から、戻ったら前の位置から
  // （会話は上の effect で最新の発言まで送る）。パネルを開き直すと忘れる
  const viewKey =
    state.view === CHAT_VIEW.STEPS
      ? `${state.view}:${state.flowId}:${subKey}`
      : state.view === CHAT_VIEW.HELP
        ? `${state.view}:${subKey}`
        : state.view === CHAT_VIEW.CONVERSATION
          ? `${state.view}:${state.conversationId ?? ''}`
          : state.view;
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;
    const positions = scrollPositions.current;
    container.scrollTop = positions.get(viewKey) ?? 0;
    const remember = () => positions.set(viewKey, container.scrollTop);
    container.addEventListener('scroll', remember, { passive: true });
    return () => container.removeEventListener('scroll', remember);
  }, [viewKey]);

  if (!chat) return null;

  const busy = runMutation.isPending || sendMutation.isPending || actionMutation.isPending;

  const recordAbandon = () => {
    if (state.view === CHAT_VIEW.STEPS && !runMutation.isPending) {
      sendChatClientEvent({ kind: 'abandon', flowId: state.flowId, step: stepRef.current });
    }
  };

  const goHome = () => {
    recordAbandon();
    setState({ view: CHAT_VIEW.HOME });
  };

  const goBack = () => {
    if (state.view === CHAT_VIEW.CONVERSATION && state.backTo === CHAT_VIEW.HISTORY) {
      setState({ view: CHAT_VIEW.HISTORY });
      return;
    }
    goHome();
  };

  /** これまでの会話・残りの内訳から、過去の会話を開く */
  const openPastConversation = (id: string, backTo?: typeof CHAT_VIEW.HISTORY) => {
    recordAbandon();
    setState({ view: CHAT_VIEW.CONVERSATION, conversationId: id, backTo });
  };

  const close = () => {
    recordAbandon();
    chat.closeChat();
  };

  const run = (flowId: string, slots: FlowSlots, extra: Partial<ChatFlowRunInput> = {}) => {
    runMutation.mutate({ flowId, body: { slots, entry, ...extra } });
  };

  const startFlow = (
    flowId: string,
    slots: Record<string, string>,
    flowEntry: NonNullable<ChatFlowRunRequest['entry']> = entry,
    parentId: string | null = null,
  ) => {
    const flow = flows.get(flowId);
    if (!flow) return;
    setSubKey('');
    if (flowId === CHAT_FLOW_ID.HOW_TO) {
      setState({ view: CHAT_VIEW.HELP });
      return;
    }
    stepRef.current = 0;
    setState({ view: CHAT_VIEW.STEPS, flowId, slots, entry: flowEntry, parentId });
    if (remainingSteps(flow, slots) === 0) {
      runMutation.mutate({ flowId, body: { slots, entry: flowEntry, parentConversationId: parentId } });
    }
  };

  const startFollowUp = (followUp: ChatFollowUp) => {
    if (conversationId) sendChatFeedback(conversationId, 'next');
    startFlow(followUp.flowId, followUp.slots ?? {}, CHAT_ENTRY_POINT.FOLLOWUP, conversationId);
  };

  const continueElsewhere = () => {
    setFollowUps([]);
    setState({ view: CHAT_VIEW.CONVERSATION, conversationId: null });
  };

  // 入力欄: ホーム・自由入力の会話・会話を続けられる用途で出す
  const inConversation = state.view === CHAT_VIEW.CONVERSATION;
  const flowConversation = inConversation && !!conversation?.flowId;
  const showInput =
    state.view === CHAT_VIEW.HOME || (inConversation && (!conversation?.flowId || !!conversationFlow?.conversational));
  const canContinue = conversation?.canContinue ?? true;
  const trimmed = draft.trim();
  const usageLevel = usage ? chatUsageLevel(usage) : CHAT_USAGE_LEVEL.OK;
  const upgradePlan = usage ? chatUpgradePlan(usage) : null;
  const disabledReason = usage?.exhausted
    ? `今月のAIの利用上限に達しました（${formatResetDate(usage.resetsAt)}に回復します）`
    : inConversation && !canContinue
      ? '会話が長くなりました。ホームから新しく始めてください'
      : draft.length > CHAT_MESSAGE_MAX_LENGTH
        ? `${CHAT_MESSAGE_MAX_LENGTH.toLocaleString()}文字以内にしてください`
        : null;
  const canSend = !!trimmed && !disabledReason && !busy;

  const send = () => {
    if (!canSend) return;
    const target = inConversation && conversation ? conversation.conversationId : null;
    sendMutation.mutate({ message: trimmed, conversationId: target });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    send();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl / Cmd + Enter で送信。日本語変換中の Enter は送らない
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const stepsFlow = state.view === CHAT_VIEW.STEPS ? flows.get(state.flowId) : undefined;
  const title =
    state.view === CHAT_VIEW.STEPS
      ? (stepsFlow?.label ?? 'AIアシスタント')
      : state.view === CHAT_VIEW.HELP
        ? '使い方を聞く'
        : state.view === CHAT_VIEW.ALL
          ? 'すべて見る'
          : state.view === CHAT_VIEW.HISTORY
            ? 'これまでの会話'
          : inConversation && conversationFlow
            ? conversationFlow.label
            : 'AIアシスタント';

  return (
    <DialogPanel className="flex h-dvh w-full flex-col bg-white shadow-xl">
      <header className="flex items-center gap-1.5 border-b border-text-100 px-3 py-2.5">
        {state.view === CHAT_VIEW.HOME ? (
          <SparklesIcon className="ml-1 h-5 w-5 text-primary-600" />
        ) : (
          <button
            type="button"
            onClick={goBack}
            className="rounded-lg p-1.5 text-text-500 hover:bg-text-100"
            aria-label={state.view === CHAT_VIEW.CONVERSATION && state.backTo ? 'これまでの会話に戻る' : 'ホームに戻る'}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
        )}
        <DialogTitle className="min-w-0 flex-1 truncate text-base font-semibold text-text-900">{title}</DialogTitle>
        <ChatUsageRing
          usage={usage}
          onNavigate={chat.closeChat}
          onOpenConversation={(id) => openPastConversation(id)}
        />
        <button type="button" onClick={close} className="rounded-lg p-1.5 text-text-500 hover:bg-text-100">
          <XMarkIcon className="h-5 w-5" />
          <span className="sr-only">閉じる</span>
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {state.view === CHAT_VIEW.HOME && usage && (
          <div className="mb-4">
            <ChatUsageNotice usage={usage} onNavigate={chat.closeChat} />
          </div>
        )}
        {state.view === CHAT_VIEW.HOME &&
          (home ? (
            <ChatHome
              home={home}
              hasPrevious={!!chat.conversationId}
              onOpenPrevious={() =>
                chat.conversationId &&
                setState({ view: CHAT_VIEW.CONVERSATION, conversationId: chat.conversationId })
              }
              onStart={(flowId, slots) => startFlow(flowId, slots)}
              onOpenAll={() => setState({ view: CHAT_VIEW.ALL })}
              onOpenHistory={() => setState({ view: CHAT_VIEW.HISTORY })}
              onClearContext={chat.clearContext}
            />
          ) : (
            <p className="text-sm text-text-500" aria-live="polite">
              {homeQuery.isError ? '読み込めませんでした。閉じて開き直してください。' : '読み込んでいます…'}
            </p>
          ))}

        {state.view === CHAT_VIEW.ALL && home && (
          <ChatAllFlows home={home} onStart={(flowId) => startFlow(flowId, {})} />
        )}

        {state.view === CHAT_VIEW.HISTORY && (
          <ChatHistory onOpen={(id) => openPastConversation(id, CHAT_VIEW.HISTORY)} />
        )}

        {state.view === CHAT_VIEW.HELP && (
          <ChatHelpTree
            busy={busy}
            onViewAnswer={(item) => sendChatClientEvent({ kind: 'help_view', flowId: CHAT_FLOW_ID.HOW_TO, item: item.id })}
            onAsk={(category, question) => run(CHAT_FLOW_ID.HOW_TO, { category, question })}
            onNavigate={chat.closeChat}
            onLevelChange={setSubKey}
          />
        )}

        {state.view === CHAT_VIEW.STEPS && stepsFlow && (
          <ChatFlowSteps
            key={`${state.flowId}-${JSON.stringify(state.slots)}`}
            flow={stepsFlow}
            events={events}
            initialSlots={state.slots}
            busy={runMutation.isPending}
            onStepChange={(index) => {
              stepRef.current = index;
              setSubKey(String(index));
            }}
            onRun={(slots) =>
              runMutation.mutate({
                flowId: state.flowId,
                body: { slots, entry: state.entry, parentConversationId: state.parentId },
              })
            }
          />
        )}

        {inConversation && (
          <div className="space-y-3">
            {conversationQuery.isLoading && conversationId && (
              <p className="text-center text-xs text-text-500">会話を読み込んでいます…</p>
            )}
            {conversationId && conversationQuery.isSuccess && conversationQuery.data === null && (
              <p className="text-sm text-text-600">
                この会話は見られません（{CHAT_RETENTION_DAYS}日を過ぎて消えたか、消した会話です）。
              </p>
            )}
            {!conversationId && !sendingText && (
              <p className="text-sm text-text-700">
                自由に頼めます。主催・共同編集しているイベントの確認や、下書きの作成・内容の変更の相談ができます。
              </p>
            )}
            {(conversation?.entries ?? []).map((item) => (
              <ChatEntryItem
                key={item.id}
                entry={item}
                activeActionId={conversation?.pendingAction?.actionId ?? null}
                busy={busy}
                onConfirm={(actionId, acknowledge) => actionMutation.mutate({ actionId, confirm: true, acknowledge })}
                onCancel={(actionId) => actionMutation.mutate({ actionId, confirm: false, acknowledge: false })}
                onNavigate={chat.closeChat}
                onEntryAction={
                  flowConversation && conversation?.flowId
                    ? (kind, target) =>
                        run(conversation.flowId as string, conversation.flowSlots ?? {}, {
                          action: kind as ChatFlowRunRequest['action'],
                          target,
                          conversationId: conversation.conversationId,
                        })
                    : undefined
                }
                onCopy={(text) => {
                  copyText(text);
                  if (conversation) sendChatFeedback(conversation.conversationId, 'copy');
                }}
                onFeedback={
                  flowConversation && conversation
                    ? (kind) => sendChatFeedback(conversation.conversationId, kind)
                    : undefined
                }
              />
            ))}
            {sendingText && (
              <>
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600/80 px-3.5 py-2 text-sm text-white">
                    <p className="whitespace-pre-wrap break-words">{sendingText}</p>
                  </div>
                </div>
                <p className="text-xs text-text-500" aria-live="polite">
                  考えています…（10秒ほどかかることがあります）
                </p>
              </>
            )}
            {runMutation.isPending && (
              <p className="text-xs text-text-500" aria-live="polite">
                準備しています…
              </p>
            )}

            {flowConversation && conversationFlow && !busy && (
              <div className="space-y-2 pt-1">
                {(conversationFlow.refinements ?? []).map((refinement) => (
                  <div key={refinement.slot} className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-text-500">
                      {conversationFlow.usesModel && (
                        <span className="mr-0.5">
                          <AiMark />
                        </span>
                      )}
                      {refinement.label}:
                    </span>
                    {refinement.options.map((option) => {
                      const current = conversation?.flowSlots?.[refinement.slot] ?? refinement.default;
                      const selected = current === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          disabled={selected}
                          onClick={() =>
                            run(conversationFlow.flowId, { ...(conversation?.flowSlots ?? {}), [refinement.slot]: option.value }, {
                              conversationId: conversation?.conversationId ?? null,
                            })
                          }
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            selected
                              ? 'border-primary-600 bg-primary-600 text-white'
                              : 'border-text-200 text-text-700 hover:bg-primary-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
                {(conversationFlow.tweaks ?? []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-text-500">
                      <span className="mr-0.5">
                        <AiMark />
                      </span>
                      直す:
                    </span>
                    {(conversationFlow.tweaks ?? []).map((tweak) => (
                      <button
                        key={tweak.label}
                        type="button"
                        disabled={!!disabledReason}
                        onClick={() =>
                          conversation &&
                          sendMutation.mutate({ message: tweak.message, conversationId: conversation.conversationId })
                        }
                        className="rounded-full border border-text-200 px-2.5 py-1 text-xs text-text-700 hover:bg-primary-50 disabled:opacity-50"
                      >
                        {tweak.label}
                      </button>
                    ))}
                  </div>
                )}
                {followUps.length > 0 && (
                  <ul className="space-y-1">
                    {followUps.map((followUp) => (
                      <li key={`${followUp.flowId}-${followUp.label}`}>
                        <button
                          type="button"
                          onClick={() => startFollowUp(followUp)}
                          className="text-sm text-primary-700 underline-offset-2 hover:underline"
                        >
                          › {followUp.label}
                          {flows.get(followUp.flowId)?.usesModel && (
                            <span className="ml-1">
                              <AiMark />
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {showInput && (
        <form
          onSubmit={handleSubmit}
          className="border-t border-text-100 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="flex items-end gap-2">
            <label htmlFor="chat-input" className="sr-only">
              AIへのメッセージ
            </label>
            <div className="relative min-w-0 flex-1">
              <textarea
                ref={inputRef}
                id="chat-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                rows={1}
                placeholder={flowConversation ? 'この内容について話す…' : undefined}
                // 文字は 16px のまま（iOS は 16px 未満の入力欄を押すと画面を拡大するため）。小さくするのは例の文だけ
                className="block min-h-[2.75rem] w-full resize-none overflow-y-auto rounded-xl border border-text-200 px-3 py-2 text-base placeholder:text-sm sm:placeholder:text-base focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              {/* 自由入力では、例を数秒おきに入れ替えて出す（何を頼めるかを見せる） */}
              {!flowConversation && draft.length === 0 && <ChatInputExamples paused={inputFocused} />}
            </div>
            <button
              type="submit"
              disabled={!canSend}
              aria-describedby="chat-send-reason"
              className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:bg-text-200 disabled:text-text-400"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              <span className="sr-only">送信</span>
              {/* 送るとAIを使う */}
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-1 ring-primary-200">
                <AiMark className="text-primary-600" />
              </span>
            </button>
          </div>
          <DisabledReason id="chat-send-reason" reason={disabledReason} align="left" />
          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-text-500">
            <span>
              {flowConversation && (
                <button type="button" onClick={continueElsewhere} className="text-primary-700 underline-offset-2 hover:underline">
                  別の会話で続ける ›
                </button>
              )}
              {!flowConversation && upgradePlan && usageLevel !== CHAT_USAGE_LEVEL.OK && state.view !== CHAT_VIEW.HOME && (
                // ホームでは上の知らせに同じ案内があるので、会話のときだけ出す
                <ChatUpgradeLink plan={upgradePlan} onNavigate={chat.closeChat} className="text-primary-700" />
              )}
              {!flowConversation && usageLevel === CHAT_USAGE_LEVEL.OK && usage?.planType === PLAN_TYPE.FREE && (
                <Link to="/pricing" onClick={chat.closeChat} className="underline">
                  有料プランならもっと使えます
                </Link>
              )}
            </span>
            <span>
              {draft.length > 0 && `${draft.length.toLocaleString()}/${CHAT_MESSAGE_MAX_LENGTH.toLocaleString()}`}
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-text-400">
            AIの答えは間違えることがあります。イベントの保存は「確定する」を押したときだけ行います。会話は
            {CHAT_RETENTION_DAYS}日後に自動で消えます。
          </p>
        </form>
      )}

      {!showInput && inConversation && flowConversation && (
        <div className="border-t border-text-100 px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-xs">
          <button type="button" onClick={continueElsewhere} className="text-primary-700 underline-offset-2 hover:underline">
            別の会話で続ける ›
          </button>
        </div>
      )}
    </DialogPanel>
  );
}

export default function ChatPanel() {
  const chat = useChat();
  const { user } = useAuth();
  // Headless UI の Dialog は iOS でピンチ操作も止めるので、2 本指のときだけ通す（Modal・BottomSheet と同じ）
  useAllowPinchZoom(!!chat?.isOpen);
  if (!chat || !user?.id) return null;
  return (
    <Dialog open={chat.isOpen} onClose={chat.closeChat} className="relative z-50">
      <div className="fixed inset-0 bg-text-900/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 flex w-full sm:max-w-md">
        {/* 開くたびにホームから始める（openCount が変わると中身を作り直す） */}
        <PanelBody key={chat.openCount} />
      </div>
    </Dialog>
  );
}
